/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable no-unsafe-optional-chaining */
import styled from '@emotion/styled'
import { PageOptions } from '@graphcommerce/framer-next-pages'
import { hygraphPageContent } from '@graphcommerce/graphcms-ui'
import { mergeDeep } from '@graphcommerce/graphql'
import { AmFaqQuestion } from '@graphcommerce/graphql-mesh'
import { Image } from '@graphcommerce/image'
import {
  AddProductsToCartFormProps,
  getProductStaticPaths,
  productPageCategory,
} from '@graphcommerce/magento-product'
import { defaultConfigurableOptionsSelection } from '@graphcommerce/magento-product-configurable'
import { StoreConfigDocument, redirectOrNotFound } from '@graphcommerce/magento-store'
import { GetStaticProps, isTypename } from '@graphcommerce/next-ui'
import { i18n } from '@lingui/core'
import { TabContext, TabPanel } from '@mui/lab'
import { Chip, NoSsr, Paper, Rating, SvgIcon, Tab, Tabs, Tooltip } from '@mui/material'
import { GetStaticPaths } from 'next'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import type { Product, Review, AggregateRating } from 'schema-dts'
import fastShipping from '../../assets/images/fast-shipping.svg'
import shipping from '../../assets/images/shipping.svg'
import { LayoutNavigation, LayoutNavigationProps } from '../../components'
import { FourOverConfProductAddToCart } from '../../components/4Over/FourOverConfProductAddToCart'
import { FourOverProductAddToCart } from '../../components/4Over/FourOverProductAddToCart'
import { Breadcrumbs } from '../../components/Breadcrumbs'
import { RelatedProducts } from '../../components/Home/RelatedProducts'
import { KlaviyoViewedProductTrackingScript } from '../../components/Klaviyo/KlaviyoViewedProductTrackingScript'
import { LoadKlaviyoActiveOnSiteScript } from '../../components/Klaviyo/LoadKlaviyoActiveOnSiteScript'
import { LayoutDocument } from '../../components/Layout/Layout.gql'
import { JsonLd } from '../../components/NextUi/JsonLd/JsonLd'
import { DeliveryCalendarPopover } from '../../components/Product/DeliveryCalendarPopover/DeliveryCalendarPopover'
import {
  jsonLdProduct,
  jsonLdProductOffer,
} from '../../components/Product/JsonLdProduct/jsonLdProduct'
import {
  jsonLdProductAggregateRating,
  jsonLdProductMultipleReviews,
  jsonLdProductReview,
} from '../../components/Product/JsonLdProduct/jsonLdProductReviews'
import { OrderProcess } from '../../components/Product/OrderProcess'
import { OrderProcessAndFAQ } from '../../components/Product/OrderProcessAndFAQ'
import { PrintTemplates } from '../../components/Product/PrintTemplates/PrintTemplates'
import { ProductPageGallery } from '../../components/Product/ProductPageGallery'
import { ProductPageMeta } from '../../components/Product/ProductPageMeta/ProductPageMeta'
import { ProductSpecs } from '../../components/Product/ProductSpecs/ProductSpecs'
import { ConfigurableContextProvider } from '../../components/ProductConfigurable/ConfigurableContext/ConfigurableContext'
import { ConfigurableProductAddToCart } from '../../components/ProductConfigurable/ConfigurableProductAddToCart'
import { ProductConfigurationGrid } from '../../components/ProductConfigurable/ProductConfigurationGrid'
import { ProductQuestionsAndAnswers } from '../../components/Reviews/ProductQuestionsAndAnswers'
import { ProductsReviewsQATabs } from '../../components/Reviews/ProductsReviewsQATabs'
import { ProductPage2Document, ProductPage2Query } from '../../graphql/ProductPage2.gql'
import { decodeHtml, formatDaysToDeliveryDisplay } from '../../hooks/displayTools'
import useCallAPI from '../../hooks/useCallAPI'
import { useResetCheckout } from '../../hooks/useResetCheckout'
import useWindowSize from '../../hooks/useWindowSize'
import { graphqlSharedClient, graphqlSsrClient } from '../../lib/graphql/graphqlSsrClient'
import templiLogo from '../../public/templi-logo.svg'
import moment from 'moment'

type Props = ProductPage2Query & Pick<AddProductsToCartFormProps, 'defaultValues'> & AmFaqQuestion

type RouteProps = { url: string }
type GetPageStaticPaths = GetStaticPaths<RouteProps>
type GetPageStaticProps = GetStaticProps<LayoutNavigationProps, Props, RouteProps>

interface StyledTabsProps {
  children?: React.ReactNode
  value: string
  onChange: (event: React.SyntheticEvent, newValue: string) => void
  variant?: 'standard' | 'scrollable' | 'fullWidth' | undefined
  scrollButtons?: boolean
  allowScrollButtonsMobile?: boolean
}

export const StyledTabs = styled((props: StyledTabsProps) => (
  <Tabs {...props} TabIndicatorProps={{ children: <span className='MuiTabs-indicatorSpan' /> }} />
))(({ theme }) => ({
  minHeight: 36,
  height: 36,
  marginLeft: '10%',
  marginRight: '10%',
  [(theme as any).breakpoints.up('md')]: {
    marginRight: '10%',
    marginLeft: 0,
  },
  '& .MuiTabs-indicator': {
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    height: 4,
  },
  '& .MuiTabs-indicatorSpan': {
    width: '100%',
    backgroundColor: '#A95CFF',
  },
}))

export interface StyledTabProps {
  label: string
  id: string
  value: string
  'aria-controls': string
  onHover?: () => void
}

export const StyledTab = styled((props: StyledTabProps) => (
  <Tab disableRipple {...props} onMouseEnter={props.onHover} />
))(({ theme }) => ({
  minWidth: 0,
  minHeight: 36,
  height: 36,
  paddingRight: 10,
  paddingLeft: 10,
  marginRight: '40px',
  textAlign: 'center',
  textTransform: 'none',
  color: '#6A6A6A',
  fontSize: 16,
  fontWeight: 500,
  [(theme as any).breakpoints.up('md')]: {
    fontSize: 20,
  },
  ':hover': {
    color: '#000000',
  },
  '&.Mui-selected': {
    fontWeight: 'bold',
    color: '#A95CFF',
    ':hover': {
      color: '#613394',
    },
  },
  '&.Mui-focusVisible': {},
}))

interface StyledTabPanelProps {
  value: string
  children?: React.ReactNode
}

export const StyledTabPanel = styled((props: StyledTabPanelProps) => <TabPanel {...props} />)(
  ({ theme }) => ({
    minHeight: 400,
    '&.MuiTabPanel-root': {
      paddingLeft: 0,
      paddingRight: 0,
    },
  }),
)
function ProductPage(props: Props) {
  const { products, relatedUpsells } = props

  useResetCheckout()

  const product = mergeDeep(products, relatedUpsells)?.items?.[0]

  const { isMobile } = useWindowSize()

  const [bottomLineData, setBottomLineData] = useState<any>()
  const router = useRouter()
  const [value, setValue] = useState('0')
  const [aggreteReviewsSchemaData, setAggreteReviewsSchemaData] =
    useState<Pick<Product, 'aggregateRating'>>()
  const [reviewsData, setReviewsData] = useState<any>()
  const [reviewsSchemaData, setReviewsSchemaData] = useState<any>()

  // const { apiCall: reviewsBottomLineDataApiCall } = useCallAPI({
  //   url: `${process.env.NEXT_PUBLIC_YOTPO_BASE_URL}/products/${process.env.NEXT_PUBLIC_YOTPO_APP_KEY}/${product?.id}/bottomline`,
  //   contentType: 'application/json',
  //   method: 'GET',
  // })

  const { apiCall: reviewsDataApiCall } = useCallAPI({
    url: `${process.env.NEXT_PUBLIC_YOTPO_API_URL}/v1/widget/${process.env.NEXT_PUBLIC_YOTPO_APP_KEY}/products/${product?.id}/reviews.json?per_page=150`,
    contentType: 'application/json',
    method: 'GET',
  })

  useEffect(() => {
    const fetchBottomLineData = async () => {
      const res = await reviewsDataApiCall()
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      setReviewsData(res?.data?.response)
    }
    fetchBottomLineData().catch((e) => {
      // addtional error handling here, the error must be set by the useCallAPI hook already
      console.error(e)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id])

  const printTemplatesSectionRef = useRef<HTMLDivElement>(null)
  const reviewsSectionRef = useRef(null)
  const handleScrollToReviews = () => {
    window.scrollTo({
      // eslint-disable-next-line no-unsafe-optional-chaining
      top: (reviewsSectionRef?.current as any)?.offsetTop - 70,
      behavior: 'smooth',
    })
  }
  const hasReviews: boolean = bottomLineData?.total_review > 0

  useEffect(() => {
    const fetchBottomLineData = async () => {
      const res = await reviewsDataApiCall()
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      setBottomLineData(res?.data?.response?.bottomline)
      setReviewsData(res?.data?.response)
    }
    fetchBottomLineData().catch((e) => {
      // addtional error handling here, the error must be set by the useCallAPI hook already
      console.error(e)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id])

  useEffect(() => {
    if (router?.asPath?.endsWith('scrollToReviews')) {
      handleScrollToReviews()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router, reviewsSectionRef.current])

  useEffect(() => {
    if (hasReviews) {
      const aggreteReviewsSchemaDataRaw = jsonLdProductAggregateRating({
        rating_value: Math.round(bottomLineData?.average_score * 10) / 10,
        review_count: bottomLineData?.total_review ?? 0,
      })
      if (reviewsData?.reviews?.length) {
        const reviewsDataRaw = jsonLdProductMultipleReviews(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          reviewsData?.reviews?.map((review) => ({
            author_name: review?.user?.display_name,
            rating_value: review.score,
            review_body: review.content,
            date_published: review.created_at, // ISO format: "2024-01-15"
          })),
        )
        setReviewsSchemaData(reviewsDataRaw)
      }
      setAggreteReviewsSchemaData(aggreteReviewsSchemaDataRaw)
    }
  }, [
    bottomLineData?.average_score,
    bottomLineData?.reviews,
    bottomLineData?.total_reviews,
    hasReviews,
    reviewsData,
  ])

  if (!product?.sku || !product.url_key) return null
  const localPath = templiLogo?.src?.replace('/', '')

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue)
  }
  const a11yProps = (index: number) => ({
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  })

  const extractYoutubeTag = (input: string) => {
    // Regex: [YT:https://www.youtube.com/...]
    const ytRegex = /\[YT:(.*?)\]/

    const match = input.match(ytRegex)
    const ytLink = match ? match[1] : null

    const cleanedText = input.replace(ytRegex, '').trim()

    return {
      cleanedText,
      ytLink,
    }
  }

  const { cleanedText: productDescription, ytLink } = extractYoutubeTag(
    product?.description?.html ?? '',
  )

  const numberOfDaysInWeek = 5
  const shippingIconDisplay =
    product.production_time && product.production_time < numberOfDaysInWeek
      ? fastShipping
      : shipping

  const setTabToSpecsAndTemplatesAndScroll = () => {
    setValue('1')
    window.scrollTo({
      // eslint-disable-next-line no-unsafe-optional-chaining
      top: (printTemplatesSectionRef as any)?.current?.offsetTop - 70,
      behavior: 'smooth',
    })
  }

  return (
    <div className='mx-5 bg-pure-white px-[10px] md:m-auto lg:max-w-maxContentWidth lg:px-[60px] lg:pt-[5px] 2xl:px-0'>
      <LoadKlaviyoActiveOnSiteScript />
      <KlaviyoViewedProductTrackingScript product={product} />
      <JsonLd
        identifier='product-page'
        item={{
          '@context': 'https://schema.org',
          ...jsonLdProduct(product),
          ...jsonLdProductOffer(product),
          ...aggreteReviewsSchemaData,
          ...reviewsSchemaData,
        }}
      />

      {isTypename(product, ['SimpleProduct']) && (
        <div>
          <ProductPageMeta {...product} />
          <Breadcrumbs product={product} />
          <div className='grid-cols-[65fr_35fr] gap-[40px] lg:grid'>
            <div className='mb-[20px] md:mb-0'>
              {isMobile && (
                <div className='my-[10px]'>
                  <h3 className='Type-XXXL-Bold'>{product.name}</h3>
                </div>
              )}
              <ProductPageGallery
                {...product}
                // Do not show small image on PDP. It will be used in product cards
                media_gallery={
                  product.media_gallery?.length && product.media_gallery?.length > 1
                    ? product.media_gallery?.filter(
                        (item) => item?.url && item?.url !== product?.small_image?.url,
                      )
                    : product.media_gallery
                }
                isMobile={isMobile}
              />
            </div>
            <Paper elevation={5} className='h-fit py-5 md:py-0'>
              <div className='mx-10 mt-0 md:my-5'>
                {!isMobile && <h1 className='Type-XXXL-Bold'>{product.name}</h1>}
                <div className='flex items-baseline justify-between'>
                  {product.production_time && (
                    <div className='flex items-center justify-center'>
                      <Image src={shippingIconDisplay} unoptimized height={12} className='mr-1' />
                      <span className='Type-XL-Regular whitespace-nowrap text-70-grey'>
                        {formatDaysToDeliveryDisplay(product?.production_time)}
                      </span>
                    </div>
                  )}
                  {hasReviews && (
                    <button
                      type='button'
                      onClick={handleScrollToReviews}
                      className='mt-[16px] flex items-center'
                    >
                      <Rating
                        name='total-ratings'
                        defaultValue={bottomLineData?.average_score}
                        size='small'
                        readOnly
                        precision={0.1}
                        className='scale-90 px-2 align-bottom'
                      />
                      <span className='Type-XL-Bold text-70-grey'>
                        ({bottomLineData?.total_reviews})
                      </span>
                    </button>
                  )}
                </div>
                <FourOverProductAddToCart
                  variables={{ sku: product.sku ?? '' }}
                  name={product.name ?? ''}
                  product={product}
                  callback={setTabToSpecsAndTemplatesAndScroll}
                />
              </div>
            </Paper>
          </div>
        </div>
      )}
      {isTypename(product, ['ConfigurableProduct']) && (
        <ConfigurableContextProvider {...product} sku={product.sku}>
          <ProductPageMeta {...product} />
          <Breadcrumbs product={product} isMobile={isMobile} />
          <div className='grid-cols-2 2xl:grid-cols-[55fr_45fr] lg:grid'>
            <div className='mb-[20px] md:mb-0'>
              {isMobile && (
                <div className='my-[10px]'>
                  {product?.free_shipping && (
                    <div className='mb-3 ml-3 flex w-full items-center justify-start gap-x-3 gap-y-2'>
                      {/* <div className='flex h-[28px] w-auto items-center justify-start rounded-[20px] border-2 border-new-templi-purple bg-[white] px-1 py-[1px] text-center text-new-templi-purple'>
                        <span className='truncate whitespace-nowrap px-2 text-[12px] font-semibold'>
                          Best Seller
                        </span>
                      </div> */}
                      {product?.free_shipping && (
                        <div className='flex h-[20px] w-auto  items-center justify-start rounded-[20px] bg-light-green px-1 py-[1px] text-center text-[#0d6216]'>
                          <span className='whitespace-nowrap px-2 text-[12px] font-semibold '>
                            Free Ship
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  <h3 className='Type-XXXL-Bold md:ml-0'>{product.name}</h3>
                  <div className='my-2 flex md:hidden w-full flex-col items-start justify-center gap-2'>
                    {hasReviews && (
                      <button
                        type='button'
                        onClick={handleScrollToReviews}
                        className='ml-[-6px] flex items-center'
                      >
                        <Rating
                          name='total-ratings'
                          defaultValue={bottomLineData?.average_score}
                          size='small'
                          readOnly
                          precision={0.1}
                          className='scale-90 align-bottom'
                        />
                        <span className='Type-Large-Bold text-70-grey'>
                          <span className='text-black'>
                            ({`${Math.round((bottomLineData?.average_score ?? 0) * 10) / 10}`})
                          </span>{' '}
                          {bottomLineData?.total_review} Reviews
                        </span>
                      </button>
                    )}
                    <div className='flex items-center gap-3'>
                      {product.est_delivery_date && !product.sku.includes('4over') && (
                        <div className='flex items-center gap-2'>
                          <span className='Type-Large-Regular whitespace-nowrap text-70-grey'>
                            Est. delivery
                          </span>
                          <span className='Type-Large-Bold whitespace-nowrap text-[#0d6216]'>
                            {moment(product?.est_delivery_date).format('MMM D')}
                          </span>
                          <DeliveryCalendarPopover
                            production_time={Number(product.production_time)}
                          />
                          <Tooltip
                            title='Estimated delivery dates are not guaranteed delivery dates. It assumes your proof is approved within 24 hours. Rush services may be available. We will make every effort possible to deliver by this date.'
                            arrow
                          >
                            <SvgIcon sx={{ fontSize: 16, color: '#555', cursor: 'pointer' }}>
                              <path d='M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z' />
                            </SvgIcon>
                          </Tooltip>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {/* {!isMobile && product?.free_shipping && (
                <div className='mt-2 flex w-full items-center justify-start gap-x-3 gap-y-2'>
                  <div className='flex h-[30px] w-auto items-center justify-start rounded-[20px] border-2 border-new-templi-purple bg-[white] px-1 py-[1px] text-center text-new-templi-purple'>
                    <span className='truncate whitespace-nowrap px-2 text-[13px] font-semibold'>
                      Best Seller
                    </span>
                  </div>
                  {product?.free_shipping && (
                    <div className='flex h-[30px] w-auto items-center justify-start rounded-[20px] border-2 border-[#0d6216] bg-[white] px-1 py-[1px] text-center text-[#0d6216]'>
                      <span className='truncate whitespace-nowrap px-2 text-[13px] font-semibold '>
                        Free Ship
                      </span>
                    </div>
                  )}
                </div>
              )} */}
              <ProductPageGallery
                {...product}
                // Do not show small image on PDP. It will be used in product cards
                media_gallery={
                  product.media_gallery?.length && product.media_gallery?.length > 1
                    ? product.media_gallery?.filter(
                        (item) => item?.url && item?.url !== product?.small_image?.url,
                      )
                    : product.media_gallery
                }
                isMobile={isMobile}
              />
            </div>
            <Paper elevation={5} className='h-fit lg:py-0'>
              <div className='mt-0 md:mb-5 md:px-[2%] lg:px-[2%] lg:pb-[10px] 2xl:pl-0 2xl:pr-[100px] xl:px-[60px] xl:pb-5'>
                {!isMobile && (
                  <h1 className='Type-XXXL-Bold lg:mb-2'>{decodeHtml(product.name ?? '')}</h1>
                )}
                {(hasReviews || product?.short_description?.html) && (
                  <div className='mt-2 flex w-full flex-col items-start justify-start gap-y-2'>
                    <div className='mb-5 hidden md:flex w-full flex-col items-start justify-center gap-2'>
                      <div className='flex items-center gap-3'>
                        {product.est_delivery_date && !product.sku.includes('4over') && (
                          <div className='flex items-center gap-2'>
                            <span className='Type-Large-Regular whitespace-nowrap text-70-grey'>
                              Est. delivery
                            </span>
                            <span className='Type-Large-Bold whitespace-nowrap text-[#0d6216]'>
                              {moment(product?.est_delivery_date).format('MMM D')}
                            </span>
                            <DeliveryCalendarPopover
                              production_time={Number(product.production_time)}
                            />
                            <Tooltip
                              title='Estimated delivery dates are not guaranteed delivery dates. It assumes your proof is approved within 24 hours. Rush services may be available. We will make every effort possible to deliver by this date.'
                              arrow
                            >
                              <SvgIcon sx={{ fontSize: 16, color: '#555', cursor: 'pointer' }}>
                                <path d='M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z' />
                              </SvgIcon>
                            </Tooltip>
                          </div>
                        )}
                        {!isMobile && product?.free_shipping && (
                          <div className='flex h-[20px] w-auto items-center justify-start rounded-[20px] bg-light-green px-1 py-[1px] text-center text-[#0d6216]'>
                            <span className='whitespace-nowrap px-2 text-[12px] font-semibold'>
                              Free Ship
                            </span>
                          </div>
                        )}
                      </div>
                      {hasReviews && (
                        <button
                          type='button'
                          onClick={handleScrollToReviews}
                          className='ml-[-6px] flex items-center'
                        >
                          <Rating
                            name='total-ratings'
                            defaultValue={bottomLineData?.average_score}
                            size='small'
                            readOnly
                            precision={0.1}
                            className='scale-90 align-bottom'
                          />
                          <span className='Type-Large-Bold text-70-grey'>
                            <span className='text-black'>
                              ({`${Math.round((bottomLineData?.average_score ?? 0) * 10) / 10}`})
                            </span>{' '}
                            {bottomLineData?.total_review} Reviews
                          </span>
                        </button>
                      )}
                    </div>
                    {product?.short_description?.html && (
                      <span
                        className='Type-XL-Regular mb-8'
                        dangerouslySetInnerHTML={{ __html: product?.short_description?.html }}
                      />
                    )}
                  </div>
                )}
                {!product?.sku?.includes('4over') && <ProductConfigurationGrid sku={product.sku} />}
                {product?.sku?.includes('4over') ? (
                  <FourOverConfProductAddToCart
                    variables={{ sku: product.sku ?? '' }}
                    name={product.name ?? ''}
                    product={product}
                    callback={setTabToSpecsAndTemplatesAndScroll}
                  />
                ) : (
                  <ConfigurableProductAddToCart
                    variables={{ sku: product.sku ?? '', quantity: 1 }}
                    name={product.name ?? ''}
                    product={product}
                    callback={setTabToSpecsAndTemplatesAndScroll}
                  />
                )}
              </div>
            </Paper>
          </div>
        </ConfigurableContextProvider>
      )}
      {/* <div className='my-5 flex w-full items-center justify-center rounded-[16px] border-[1px] border-light-gray'>
        <div className='mx-[40px] my-[20px] w-full' ref={printTemplatesSectionRef}>
          <TabContext value={value}>
            <StyledTabs
              value={value}
              onChange={handleChange}
              variant='scrollable'
              scrollButtons
              allowScrollButtonsMobile
            >
              <StyledTab
                label='Description'
                {...a11yProps(0)}
                value='0'
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              />
              <StyledTab
                label='Specs & Templates'
                {...a11yProps(1)}
                value='1'
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              />
              <StyledTab
                label={`FAQ's`}
                {...a11yProps(2)}
                value='2'
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              />
            </StyledTabs>
            <StyledTabPanel value='0'>
              <div className='mx-[40px] my-[20px]'>
                {productDescription && (
                  <span
                    className='Type-XL-Regular'
                    dangerouslySetInnerHTML={{ __html: product?.description?.html }}
                  />
                )}
              </div>
            </StyledTabPanel>
            <StyledTabPanel value='1'>
              <div className='m-[20px] flex flex-col gap-x-12 gap-y-5 md:grid md:grid-cols-2'>
                <PrintTemplates amfiles={product.amfiles} />
                <ProductSpecs aggregations={products?.aggregations} product={product} />
              </div>
            </StyledTabPanel>
            <StyledTabPanel value='2'>
              <div className='mt-6'>
                <ProductQuestionsAndAnswers
                  productId={product.id}
                  productTitle={product.name}
                  productUrl={product.url_key}
                  qaData={product.amfaq_questions ?? []}
                />
              </div>
            </StyledTabPanel>
          </TabContext>
        </div>
      </div> */}
      <div className='my-5 flex w-full items-center justify-center rounded-[16px] border-[1px] border-light-gray'>
        <div className='m-4 flex flex-col gap-x-12 gap-y-12 md:m-8 md:grid md:grid-cols-[55fr_45fr] md:gap-y-5'>
          <div className='md:border-r md:border-light-gray md:pr-10'>
            <PrintTemplates amfiles={product.amfiles} />
          </div>
          <ProductSpecs aggregations={products?.aggregations} product={product} />
        </div>
      </div>
      {product?.video && (
        <div className='my-5 flex w-full flex-col items-center justify-center rounded-[16px] bg-[#FFFDF3CC] p-6 md:p-10'>
          <div className='w-full flex justify-center'>
            <div className='relative w-full max-w-[900px] aspect-[16/9] rounded-[12px] overflow-hidden'>
              {product?.video?.includes('s3.amazonaws.com') || product?.video?.endsWith('.mp4') ? (
                <video
                  key={product.video}
                  width='100%'
                  height='100%'
                  autoPlay
                  loop
                  muted
                  playsInline
                  controls={false}
                  className='absolute top-0 left-0 w-full h-full object-cover'
                >
                  <source src={product.video} type='video/mp4' />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <iframe
                  src={`${product?.video}${
                    product?.video?.includes('?') ? '&' : '?'
                  }autoplay=1&mute=1&controls=0&modestbranding=1&rel=0&showinfo=0&loop=1&playlist=${
                    product?.video?.split('/').pop()?.split('?')[0]
                  }&iv_load_policy=3&disablekb=1&playsinline=1`}
                  title='YouTube video player'
                  allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                  allowFullScreen
                  frameBorder='0'
                  className='absolute top-0 left-0 w-full h-full object-cover'
                />
              )}
            </div>
          </div>
        </div>
      )}
      {product?.amfaq_questions && product?.amfaq_questions?.length > 0 && (
        <div className='my-5 flex w-full flex-col items-center justify-center rounded-[16px] border-[1px] border-light-gray p-4 md:p-8'>
          <h3 className='Type-XXL-Medium w-full text-start'>{i18n?._('FAQs')}</h3>
          <ProductQuestionsAndAnswers
            productId={product.id}
            productTitle={product.name}
            productUrl={product.url_key}
            qaData={product.amfaq_questions ?? []}
          />
        </div>
      )}
      {/* {product?.requires_artwork && (
        <div className='mb-10'>
          <OrderProcessAndFAQ />
        </div>
      )} */}
      {product?.requires_artwork && (
        <div className='mb-10'>
          <OrderProcess />
        </div>
      )}
      <NoSsr>
        <ProductsReviewsQATabs
          productId={product.id}
          productTitle={product.name}
          productUrl={product.url_key}
          sectionRef={reviewsSectionRef}
          productDescription={product.description}
          productImages={product.media_gallery ?? []}
          bottomLineData={bottomLineData}
          qaData={product.amfaq_questions ?? []}
        />
      </NoSsr>
      <RelatedProducts className='mb-10' noHorizontalMargins product_uid={product?.uid} />
    </div>
  )
}

ProductPage.pageOptions = {
  Layout: LayoutNavigation,
} as PageOptions

export default ProductPage

export const getStaticPaths: GetPageStaticPaths = async ({ locales = [] }) => {
  if (import.meta.graphCommerce.legacyProductRoute) return { paths: [], fallback: false }
  if (process.env.NODE_ENV === 'development' || process.env.SKIP_ISG)
    return { paths: [], fallback: 'blocking' }

  const path = (locale: string) => getProductStaticPaths(graphqlSsrClient(locale), locale)
  const paths = (await Promise.all(locales.map(path))).flat(1)

  return { paths, fallback: 'blocking' }
}

export const getStaticProps: GetPageStaticProps = async ({ params, locale }) => {
  if (import.meta.graphCommerce.legacyProductRoute) return { notFound: true }

  const client = graphqlSharedClient(locale)
  const staticClient = graphqlSsrClient(locale)

  const urlKey = params?.url ?? '??'

  const conf = client.query({ query: StoreConfigDocument })
  const productPage = staticClient.query({ query: ProductPage2Document, variables: { urlKey } })
  const layout = staticClient.query({ query: LayoutDocument, fetchPolicy: 'cache-first' })

  const product = productPage.then((pp) =>
    pp.data.products?.items?.find((p) => p?.url_key === urlKey),
  )

  const pages = hygraphPageContent(staticClient, 'product/global', product, true)
  if (!(await product)) return redirectOrNotFound(staticClient, conf, params, locale)

  const category = productPageCategory(await product)
  const up =
    category?.url_path && category?.name
      ? { href: `/${category.url_path}`, title: category.name }
      : { href: `/`, title: 'Home' }

  return {
    props: {
      ...defaultConfigurableOptionsSelection(urlKey, client, (await productPage).data),
      ...(await layout).data,
      ...(await pages).data,
      apolloState: await conf.then(() => client.cache.extract()),
      up,
    },
    revalidate: process.env.SKIP_ISG ? 1 : 60 * 5,
  }
}
